import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "./icons";
import { markdownBlocks, markdownInline } from "../../../lib/artemis/markdown-lite";
import {
  DEFAULT_LIMITS, REWRITE_REFRESH_DELAYS, awaitingRewrite, bodyPreview, capitalise, emptySkillForm, enabledLabel, formFromSkill,
  isRefused, skillFormErrors, skillPayload, skillReading, startsRewrite
} from "../../../lib/artemis/skills";

// A user's own Artemis skills (WEB-7124, API-3688): how they hunt, which Artemis follows on later
// questions. Listed on the welcome screen and in a Skills view, added and edited in a form, and
// shown as a card when Artemis saves one from the chat.

// { skills, limits, available, loading, error, refresh, refreshSoon, create, update, remove, byId }.
// api holds the dispatched Skills actions. available is false where the server has no Skills (a 404).
export const useUserSkills = api => {
  const [skills, setSkills] = useState([])
  const [limits, setLimits] = useState(DEFAULT_LIMITS)
  const [available, setAvailable] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const refresh = useCallback(() => {
    setLoading(true)
    return api.list()
      .then(page => {
        setSkills((page && page.Items) || [])
        // Limits come from the server (they've been raised once already), with defaults as a floor.
        setLimits({ ...DEFAULT_LIMITS, ...((page && page.Limits) || {}) })
        setLoaded(true)
      })
      .catch(err => { if (err && (err.status === 404 || err.code === 'http_404')) setAvailable(false) })
      .finally(() => setLoading(false))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  // A save returns Optimized false and Artemis rewrites the skill in the background (API-3801), so
  // look again a few seconds later, and once more only if skill id still hasn't been rewritten.
  const skillsRef = useRef(skills)
  skillsRef.current = skills
  const timers = useRef([])
  useEffect(() => () => timers.current.forEach(clearTimeout), [])
  const refreshSoon = useCallback(id => {
    timers.current.forEach(clearTimeout)
    timers.current = REWRITE_REFRESH_DELAYS.map(ms => setTimeout(() => {
      const skill = skillsRef.current.find(s => s.Id === id)
      if (!skill || awaitingRewrite(skill)) refresh()
    }, ms))
  }, [refresh])

  const create = useCallback(skill => api.create(skill).then(saved => {
    refresh()
    if (saved && saved.Id != null) refreshSoon(saved.Id)
    return saved
  }), [api, refresh, refreshSoon])
  const update = useCallback((id, patch) => api.update(id, patch).then(saved => {
    setSkills(list => list.map(s => (s.Id === id ? { ...s, ...(saved || patch) } : s)))
    if (startsRewrite(patch, { Id: id })) refreshSoon(id)
    return saved
  }), [api, refreshSoon])
  const remove = useCallback(id => api.remove(id).then(() => setSkills(list => list.filter(s => s.Id !== id))), [api])
  const byId = useMemo(() => new Map(skills.map(s => [s.Id, s])), [skills])

  return { skills, limits, available, loading, loaded, refresh, refreshSoon, create, update, remove, byId }
}

const errorText = err => (err && err.userMessage) || 'Couldn’t save that. Try again.'

const Switch = ({ on, label, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
    className="artemis-switch-button" onClick={() => onChange(!on)}>
    <span className={`artemis-switch ${on ? 'artemis-switch-on' : ''}`} aria-hidden="true"><span /></span>
  </button>
)

// Artemis refused the skill's text (API-3801): it stays saved and on, but isn't used until edited.
const RefusedNote = ({ skill, onEdit }) => (
  <p className="artemis-skill-refused" role="note">
    <span>Artemis won’t use this as written: it doesn’t read as a hunting method.</span>
    {onEdit ? <> <button type="button" className="artemis-link-button" onClick={() => onEdit(skill)}>Edit it</button> to describe how you hunt.</> : ' Edit it in the Artemis tab.'}
  </p>
)

// A line of the rewrite with its `code` (tool names) and **bold** spans.
const Inline = ({ text }) => markdownInline(text).map((p, i) => (p.code ? <code key={i}>{p.text}</code> : p.bold ? <strong key={i}>{p.text}</strong> : p.text))

// The rewrite's Markdown, minimally (as on iOS and Android): headings as bold lines, lists as lists, code
// spans in monospace. Built as elements, so nothing in it runs as HTML.
export const SkillMarkdown = ({ text, className = '' }) => (
  <div className={`artemis-skill-md ${className}`}>
    {markdownBlocks(text).map((b, i) => (b.type === 'heading'
      ? <p key={i} className="artemis-skill-md-heading"><Inline text={b.text} /></p>
      : b.type === 'list'
        ? (b.ordered
          ? <ol key={i}>{b.items.map((t, j) => <li key={j}><Inline text={t} /></li>)}</ol>
          : <ul key={i}>{b.items.map((t, j) => <li key={j}><Inline text={t} /></li>)}</ul>)
        : <p key={i}><Inline text={b.text} /></p>))}
  </div>
)

// "How Artemis reads this": the rewrite Artemis follows (API-3801), collapsed, its Markdown rendered minimally.
const SkillReading = ({ skill }) => {
  const reading = skillReading(skill)
  if (!reading) return null
  return (
    <details className="artemis-skill-reading">
      <summary>How Artemis reads this</summary>
      {reading.appliesWhen && <div className="artemis-skill-reading-when"><span className="artemis-skill-reading-label">Uses it when</span><SkillMarkdown text={reading.appliesWhen} /></div>}
      {reading.body && <SkillMarkdown text={reading.body} className="artemis-skill-reading-body" />}
      {reading.tools.length > 0 && (
        <div className="artemis-step-chips" aria-label="Tools it uses">
          {reading.tools.map(t => <span key={t} className="artemis-step-chip">{t}</span>)}
        </div>
      )}
    </details>
  )
}

// One row: title, when it applies, on/off, Edit. With detail (the Skills view), also how Artemis
// reads it.
const SkillRow = ({ skill, onToggle, onEdit, onDelete, detail }) => {
  const [confirming, setConfirming] = useState(false)
  const refused = isRefused(skill)
  const reading = detail && !!skillReading(skill)
  if (confirming) {
    return (
      <div className="artemis-card-button artemis-card-confirm" role="group" aria-label={`Delete ${skill.Title}`}>
        <span className="artemis-flex">Delete “{capitalise(skill.Title)}”?</span>
        <button type="button" className="artemis-small-button" onClick={() => setConfirming(false)}>Cancel</button>
        <button type="button" className="artemis-small-button artemis-small-button-danger" onClick={() => onDelete(skill)}>Delete</button>
      </div>
    )
  }
  return (
    <div className={`artemis-skill-row ${skill.Enabled === false ? 'artemis-skill-off' : ''} ${refused ? 'artemis-skill-row-refused' : ''} ${refused || reading ? 'artemis-skill-row-wrap' : ''}`}>
      <span className="artemis-card-icon"><Icon name="skill" size={16} /></span>
      <span className="artemis-card-text">
        <span className="artemis-card-title">{capitalise(skill.Title)}</span>
        {skill.AppliesWhen && <span className="artemis-card-sub">{skill.AppliesWhen}</span>}
      </span>
      <Switch on={skill.Enabled !== false} label={`Use ${skill.Title}`} onChange={on => onToggle(skill, on)} />
      <button type="button" className="artemis-icon-button" title="Edit" aria-label={`Edit ${skill.Title}`} onClick={() => onEdit(skill)}><Icon name="edit" size={16} /></button>
      {onDelete && (
        <button type="button" className="artemis-icon-button" title="Delete" aria-label={`Delete ${skill.Title}`} onClick={() => setConfirming(true)}><Icon name="trash" size={16} /></button>
      )}
      {refused && <RefusedNote skill={skill} onEdit={onEdit} />}
      {reading && <SkillReading skill={skill} />}
    </div>
  )
}

// On the welcome screen: up to three skills, Add, and Manage when there are more.
export const WelcomeSkills = ({ skills, onAdd, onManage, onToggle, onEdit }) => (
  <div className="artemis-card-list">
    <span className="artemis-eyebrow">Your skills</span>
    {skills.length === 0 && (
      <p className="artemis-muted artemis-skills-hint">Tell Artemis how you hunt, like how you glass or play the wind, and it follows it on later questions.</p>
    )}
    {skills.slice(0, 3).map(s => <SkillRow key={s.Id} skill={s} onToggle={onToggle} onEdit={onEdit} />)}
    <div className="artemis-skills-actions">
      <button type="button" className="artemis-small-button artemis-small-button-go" onClick={onAdd}>Add a skill</button>
      {skills.length > 0 && <button type="button" className="artemis-small-button" onClick={onManage}>{skills.length > 3 ? `All ${skills.length}` : 'Manage'}</button>}
    </div>
  </div>
)

// The Skills view: all of them, with on/off, Edit and Delete.
export const SkillsView = ({ skills, limits, onAdd, onToggle, onEdit, onDelete, onBack, notice }) => (
  <>
    <div className="artemis-view-title">
      <button type="button" className="artemis-icon-button" title="Back" aria-label="Back" onClick={onBack}><Icon name="back" size={18} /></button>
      <h2>Your skills</h2>
    </div>
    <p className="artemis-muted">Artemis follows these when a question fits “when to use it”. {enabledLabel(skills, limits)}.</p>
    {notice && <p className="artemis-dictation-note" role="status">{notice}</p>}
    <div className="artemis-card-list">
      {skills.map(s => <SkillRow key={s.Id} skill={s} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} detail />)}
      <button type="button" className="artemis-small-button artemis-small-button-go artemis-load-more" onClick={onAdd} disabled={skills.length >= limits.MaxSkills}>Add a skill</button>
    </div>
  </>
)

const Field = ({ label, hint, error, count, max, children }) => (
  <label className="artemis-field">
    <span className="artemis-field-label">{label}{max ? <span className={`artemis-field-count ${count > max ? 'artemis-field-over' : ''}`}>{count.toLocaleString('en-US')}/{max.toLocaleString('en-US')}</span> : null}</span>
    {hint && <span className="artemis-field-hint">{hint}</span>}
    {children}
    {error && <span className="artemis-field-error">{error}</span>}
  </label>
)

// Add or edit a skill. skill is the one being edited, or null for a new one. builtIns are the
// built-in skills to start from ({ Name, Title, Description }).
export const SkillForm = ({ skill, limits, builtIns, loadBuiltIn, onSave, onCancel }) => {
  const [form, setForm] = useState(() => (skill ? formFromSkill(skill) : emptySkillForm()))
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)
  const errors = skillFormErrors(form, limits)
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  // "Make this mine": starting from a built-in fills in its title, its description as when to use
  // it, and its text, all to edit. BasedOn only records where it came from.
  const [loadingBuiltIn, setLoadingBuiltIn] = useState(false)
  const startFrom = name => {
    if (!name) return setForm(f => ({ ...f, basedOn: null }))
    const listed = builtIns.find(x => x.Name === name) || { Name: name }
    const fill = b => setForm(f => ({
      ...f,
      basedOn: name,
      title: capitalise(b.Title || b.Name),
      appliesWhen: b.Description || f.appliesWhen,
      body: b.Body || f.body
    }))
    fill(listed)
    if (!loadBuiltIn) return
    setLoadingBuiltIn(true)
    loadBuiltIn(name).then(full => { if (full) fill(full) }).catch(() => {}).finally(() => setLoadingBuiltIn(false))
  }

  const save = () => {
    setTouched(true)
    if (Object.keys(errors).length) return
    const payload = skillPayload(form, skill, limits)
    if (skill && Object.keys(payload).length === 0) return onCancel()
    setSaving(true)
    setServerError(null)
    onSave(payload).catch(err => setServerError(errorText(err))).finally(() => setSaving(false))
  }

  return (
    <>
      <div className="artemis-view-title">
        <button type="button" className="artemis-icon-button" title="Back" aria-label="Back" onClick={onCancel}><Icon name="back" size={18} /></button>
        <h2>{skill ? 'Edit skill' : 'Add a skill'}</h2>
      </div>
      <div className="artemis-skill-form">
        {!skill && builtIns.length > 0 && (
          <Field label="Start from one of Artemis’s skills (optional)" hint={loadingBuiltIn ? 'Loading it…' : form.basedOn ? 'Change anything to make it yours.' : null}>
            <select className="artemis-input-field" value={form.basedOn || ''} disabled={loadingBuiltIn} onChange={e => startFrom(e.target.value)}>
              <option value="">Start from scratch</option>
              {builtIns.map(b => <option key={b.Name} value={b.Name}>{capitalise(b.Title || b.Name)}</option>)}
            </select>
          </Field>
        )}
        <Field label="Name" error={touched && errors.title} count={form.title.length} max={limits.MaxTitleLength}>
          <input className="artemis-input-field" value={form.title} onChange={set('title')} placeholder="Late-season elk glassing" />
        </Field>
        <Field label="When to use it" hint="Artemis uses the skill when a question fits this." error={touched && errors.appliesWhen} count={form.appliesWhen.length} max={limits.MaxAppliesWhenLength}>
          <textarea className="artemis-input-field" rows={2} value={form.appliesWhen} onChange={set('appliesWhen')} placeholder="When I ask where or when to glass for elk after the rut" />
        </Field>
        <Field label="How you hunt" hint="What Artemis should know and do, in your words." error={touched && errors.body} count={form.body.length} max={limits.MaxBodyLength}>
          <textarea className="artemis-input-field" rows={8} value={form.body} onChange={set('body')}
            placeholder="I glass from first light for two hours from the ridge above the timber, then move to north-facing benches. I hunt alone and pack in no more than 4 miles…" />
        </Field>
        <label className="artemis-checkbox-row">
          <input type="checkbox" checked={form.enabled} onChange={set('enabled')} /> Turn it on
        </label>
        {serverError && <p className="artemis-field-error" role="alert">{serverError}</p>}
        <div className="artemis-feedback-actions">
          <button type="button" className="artemis-small-button" onClick={onCancel}>Cancel</button>
          <button type="button" className="artemis-primary-button" onClick={save} disabled={saving}>{saving ? 'Saving…' : skill ? 'Save' : 'Add skill'}</button>
        </div>
      </div>
    </>
  )
}

// In the chat: the skill Artemis just saved (create_skill / update_skill), as it now stands.
// Without onEdit (the side panel) it's read only.
export const UserSkillCard = ({ card, current, onEdit, onToggle, onDelete }) => {
  const [confirming, setConfirming] = useState(false)
  const skill = current || card.skill
  const deleted = !!card.skill && current === null
  return (
    <section className="artemis-hunt-card artemis-card-tone-map" aria-label={`Your skill ${skill.Title}`}>
      <div className="artemis-hunt-card-header">
        <Icon name="skill" size={15} />
        <span className="artemis-hunt-card-title">{deleted ? 'Deleted your skill' : card.action === 'updated' ? 'Updated your skill' : 'Saved your skill'}</span>
        {!deleted && isRefused(skill) && <span className="artemis-hunt-badge artemis-badge-refused">Not used</span>}
        {!deleted && skill.Enabled === false && <span className="artemis-hunt-badge artemis-badge-off">Off</span>}
      </div>
      <div className="artemis-card-section">
        <span className="artemis-hunt-name">{capitalise(skill.Title)}</span>
        {skill.AppliesWhen && <span className="artemis-hunt-sub">When: {skill.AppliesWhen}</span>}
        {skill.Body && <p className="artemis-skill-preview">{bodyPreview(skill.Body)}</p>}
        {!deleted && isRefused(skill) && <RefusedNote skill={skill} onEdit={onEdit} />}
        {!deleted && <SkillReading skill={skill} />}
      </div>
      {!deleted && onEdit && (
        <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
          {confirming ? (
            <>
              <span className="artemis-flex">Delete this skill?</span>
              <button type="button" className="artemis-small-button" onClick={() => setConfirming(false)}>Cancel</button>
              <button type="button" className="artemis-small-button artemis-small-button-danger" onClick={() => onDelete(skill)}>Delete</button>
            </>
          ) : (
            <>
              <span className="artemis-flex" />
              <button type="button" className="artemis-small-button" onClick={() => onEdit(skill)}>Edit</button>
              <button type="button" className="artemis-small-button" onClick={() => onToggle(skill, skill.Enabled === false)}>{skill.Enabled === false ? 'Turn on' : 'Turn off'}</button>
              <button type="button" className="artemis-small-button" onClick={() => setConfirming(true)}>Delete</button>
            </>
          )}
        </div>
      )}
      {(deleted || !onEdit) && <div className="artemis-card-pad" />}
    </section>
  )
}
